<template>

    <Head title="Settings"/>

    <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">Settings</h1>

    <SettingsTabs/>

    <div v-if="$page.props.flash.success" class="mt-4 rounded-md bg-green-50 p-4 text-sm text-green-700">
        {{ $page.props.flash.success }}
    </div>

    <form class="mt-8 max-w-2xl" @submit.prevent="submit">
        <div class="border-b border-gray-200 pb-6">
            <h2 class="text-lg font-medium text-gray-900">Authentication</h2>
            <p class="mt-1 text-sm text-gray-500">
                Enable single sign-on providers for the login page. Users must already exist in the system.
            </p>
        </div>

        <div class="mt-6 space-y-6">
            <div class="flex items-center justify-between">
                <div>
                    <label class="block text-sm font-medium text-gray-900">Enable Google SSO</label>
                    <p class="text-sm text-gray-500">Allow users to sign in with Google.</p>
                </div>
                <button
                    type="button"
                    @click="form.google_enabled = !form.google_enabled"
                    :class="{'bg-pitch-purple': form.google_enabled, 'bg-gray-200': !form.google_enabled }"
                    class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pitch-purple"
                    role="switch"
                    :aria-checked="form.google_enabled"
                >
                    <span class="sr-only">Enable Google SSO</span>
                    <span
                        aria-hidden="true"
                        :class="[form.google_enabled ? 'translate-x-5': 'translate-x-0' ]"
                        class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                    ></span>
                </button>
            </div>

            <div class="flex items-center justify-between">
                <div>
                    <label class="block text-sm font-medium text-gray-900">Enable Microsoft SSO</label>
                    <p class="text-sm text-gray-500">Allow users to sign in with Microsoft Azure.</p>
                </div>
                <button
                    type="button"
                    @click="form.microsoft_enabled = !form.microsoft_enabled"
                    :class="{'bg-pitch-purple': form.microsoft_enabled, 'bg-gray-200': !form.microsoft_enabled }"
                    class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pitch-purple"
                    role="switch"
                    :aria-checked="form.microsoft_enabled"
                >
                    <span class="sr-only">Enable Microsoft SSO</span>
                    <span
                        aria-hidden="true"
                        :class="[form.microsoft_enabled ? 'translate-x-5': 'translate-x-0' ]"
                        class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                    ></span>
                </button>
            </div>

            <div class="flex items-center justify-between border-t border-gray-200 pt-6">
                <div>
                    <label class="block text-sm font-medium text-gray-900">Force SSO login</label>
                    <p class="text-sm text-gray-500">Hide email and password login. Requires at least one SSO provider to be enabled.</p>
                    <p v-if="form.errors.force_sso_enabled" class="mt-1 text-sm text-red-600">{{ form.errors.force_sso_enabled }}</p>
                </div>
                <button
                    type="button"
                    @click="toggleForceSso"
                    :disabled="!form.google_enabled && !form.microsoft_enabled"
                    :class="{
                        'bg-pitch-purple': form.force_sso_enabled,
                        'bg-gray-200': !form.force_sso_enabled,
                        'opacity-50 cursor-not-allowed': !form.google_enabled && !form.microsoft_enabled,
                    }"
                    class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pitch-purple"
                    role="switch"
                    :aria-checked="form.force_sso_enabled"
                >
                    <span class="sr-only">Force SSO login</span>
                    <span
                        aria-hidden="true"
                        :class="[form.force_sso_enabled ? 'translate-x-5': 'translate-x-0' ]"
                        class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                    ></span>
                </button>
            </div>
        </div>

        <div class="mt-8">
            <button
                type="submit"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pitch-purple"
                :class="{ 'opacity-25': form.processing }"
                :disabled="form.processing"
            >
                Save settings
            </button>
        </div>
    </form>

</template>


<script>

import {Head, useForm} from "@inertiajs/vue3";
import {watch} from "vue";
import SettingsTabs from "@/Components/SettingsTabs";
import Authenticated from "@/Layouts/Authenticated";

export default {
    layout: Authenticated,
    components: {
        SettingsTabs,
        Head,
    },
    props: {
        settings: {
            type: Object,
            required: true,
        },
    },
    setup(props) {
        const form = useForm({
            google_enabled: props.settings.google_enabled,
            microsoft_enabled: props.settings.microsoft_enabled,
            force_sso_enabled: props.settings.force_sso_enabled,
        });

        watch(
            () => [form.google_enabled, form.microsoft_enabled],
            () => {
                if (!form.google_enabled && !form.microsoft_enabled) {
                    form.force_sso_enabled = false;
                }
            }
        );

        const toggleForceSso = () => {
            if (!form.google_enabled && !form.microsoft_enabled) {
                return;
            }

            form.force_sso_enabled = !form.force_sso_enabled;
        };

        const submit = () => {
            form.post(route('admin.settings.update'));
        };

        return { form, submit, toggleForceSso };
    },
}
</script>
